import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import Filters from "../components/Filters";
import { ArrowRight, ShieldCheck, MapPinned, HeartHandshake } from "lucide-react";

const HomePage = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [zones, setZones] = useState([]);
  const [filters, setFilters] = useState({ contract: "all", type: "all", zone: "all", max_price: "" });

  useEffect(() => {
    api.get("/properties?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const submitFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== "all") params.set(k, v);
    });
    navigate(`/immobili?${params.toString()}`);
  };

  return (
    <div data-testid="home-page" className="fade-in">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1760681556302-69e76e476b27?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Toscana"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/10" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-32 md:pb-40 w-full">
          <div className="max-w-3xl text-white fade-up">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#CBA37E] mb-6">
              {t.hero.eyebrow}
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight whitespace-pre-line mb-7">
              {t.hero.title}
            </h1>
            <p className="text-lg text-white/85 max-w-xl leading-relaxed mb-10">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/immobili"
                data-testid="hero-cta-properties"
                className="bg-[#8B5A3C] text-white hover:bg-[#704830] transition-colors rounded-full px-8 py-3.5 font-medium inline-flex items-center gap-2"
              >
                {t.hero.ctaProperties} <ArrowRight size={16} />
              </Link>
              <Link
                to="/valutazione"
                data-testid="hero-cta-evaluation"
                className="bg-white/10 backdrop-blur border-2 border-white/40 text-white hover:bg-white hover:text-[#2C2621] transition-colors rounded-full px-8 py-3.5 font-medium"
              >
                {t.hero.ctaEvaluation}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS - overlapping */}
      <section className="px-6 md:px-10">
        <Filters values={filters} onChange={setFilters} onSubmit={submitFilters} zones={zones} />
      </section>

      {/* FEATURED PROPERTIES */}
      <section data-testid="featured-section" className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#8B5A3C] mb-4">
              {t.featured.eyebrow}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight tracking-tight text-[#2C2621] mb-5">
              {t.featured.title}
            </h2>
            <p className="text-[#5C544B] text-base leading-relaxed">{t.featured.subtitle}</p>
          </div>
          <Link
            to="/immobili"
            data-testid="featured-view-all"
            className="self-start md:self-end inline-flex items-center gap-2 text-sm font-medium text-[#8B5A3C] hover:text-[#704830] border-b border-[#8B5A3C] pb-1 transition-colors whitespace-nowrap"
          >
            {t.featured.viewAll} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {featured.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section data-testid="about-teaser" className="bg-[#F3E6DA] relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#8B5A3C] mb-4">
              {t.about.eyebrow}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight tracking-tight text-[#2C2621] whitespace-pre-line mb-6">
              {t.about.title}
            </h2>
            <p className="text-[#5C544B] text-base leading-relaxed mb-5">{t.about.p1}</p>
            <p className="text-[#5C544B] text-base leading-relaxed mb-8">{t.about.p2}</p>
            <Link
              to="/chi-siamo"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#8B5A3C] hover:text-[#704830] border-b border-[#8B5A3C] pb-1 transition-colors"
            >
              {t.about.cta} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="https://images.pexels.com/photos/19433237/pexels-photo-19433237.jpeg?auto=compress&cs=tinysrgb&w=940"
              alt="Pisa"
              className="rounded-2xl object-cover h-72 w-full col-span-2"
            />
            <div className="bg-white rounded-2xl p-6 col-span-2 sm:col-span-1 flex flex-col justify-center">
              <div className="font-display text-5xl text-[#8B5A3C] leading-none">10+</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#5C544B] mt-2 font-bold">{lang === "it" ? "Anni di esperienza" : "Years of experience"}</div>
            </div>
            <div className="bg-[#2C2621] text-white rounded-2xl p-6 col-span-2 sm:col-span-1 flex flex-col justify-center">
              <div className="font-display text-5xl text-[#CBA37E] leading-none">5</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#CBA37E]/80 mt-2 font-bold">{lang === "it" ? "Città di esperienza" : "Cities of experience"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, ...t.about.values[0] },
            { icon: MapPinned, ...t.about.values[1] },
            { icon: HeartHandshake, ...t.about.values[2] },
          ].map((v, i) => (
            <div key={i} className="bg-white border border-[#E8DFD8] rounded-2xl p-8 hover:border-[#8B5A3C] transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center mb-5">
                <v.icon size={20} className="text-[#8B5A3C]" />
              </div>
              <h3 className="font-display text-xl font-medium text-[#2C2621] mb-3">{v.title}</h3>
              <p className="text-sm text-[#5C544B] leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EVALUATION CTA */}
      <section data-testid="evaluation-cta" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/31854903/pexels-photo-31854903.jpeg?auto=compress&cs=tinysrgb&w=2000"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C2621]/90 via-[#2C2621]/80 to-[#2C2621]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-36 text-white">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#CBA37E] mb-5">
              {t.evaluation.eyebrow}
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight mb-6">
              {t.evaluation.title}
            </h2>
            <p className="text-white/85 text-lg leading-relaxed mb-9">{t.evaluation.subtitle}</p>
            <Link
              to="/valutazione"
              data-testid="evaluation-cta-button"
              className="inline-flex items-center gap-2 bg-[#CBA37E] text-[#2C2621] hover:bg-white transition-colors rounded-full px-8 py-4 font-semibold"
            >
              {t.evaluation.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
